import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Linkedin, Twitter, Github, Mail, ArrowUpRight } from 'lucide-react';
import { NAV_LINKS, COMPANY } from '../mock';

const FooterLogo = () => (
  <div className="flex items-center gap-2.5">
    <img
      src="/scatura-constellation.png"
      alt="Scatura"
      className="w-12 h-12 rounded-xl object-contain bg-[#020617]"
    />
    <div className="leading-tight">
      <div className="font-display text-[16px] font-semibold text-white tracking-tight">
        Scatura
      </div>
      <div className="text-[10px] text-slate-400 uppercase tracking-[0.18em] font-medium">
        Solutions
      </div>
    </div>
  </div>
);

const Footer = () => {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const resolveHref = (href) => {
    if (href.startsWith('#')) return isHome ? href : `/${href}`;
    return href;
  };

  return (
    <footer className="bg-slate-950 text-slate-400">
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <FooterLogo />
            <p className="mt-5 text-[14.5px] leading-relaxed max-w-sm">
              A boutique engineering studio for intelligent automation, chaos engineering, self-healing systems and app modernization - plus the training to keep the muscle memory sharp.
            </p>
            <a
              href={`mailto:${COMPANY.email}`}
              className="mt-6 inline-flex items-center gap-1.5 text-white font-medium text-[14.5px] hover:text-indigo-300 transition-colors"
            >
              {COMPANY.email}
              <ArrowUpRight size={15} />
            </a>
          </div>

          <div>
            <div className="text-white font-semibold text-[13px] uppercase tracking-[0.16em]">
              Explore
            </div>
            <ul className="mt-4 space-y-2.5">
              {NAV_LINKS.map((l) => {
                const isRoute = l.href.startsWith('/');
                if (isRoute) {
                  return (
                    <li key={l.label}>
                      <Link to={l.href} className="text-[14px] hover:text-white transition-colors">
                        {l.label}
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={l.label}>
                    <a href={resolveHref(l.href)} className="text-[14px] hover:text-white transition-colors">
                      {l.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <div className="text-white font-semibold text-[13px] uppercase tracking-[0.16em]">
              Practices
            </div>
            <ul className="mt-4 space-y-2.5 text-[14px]">
              <li><a href="#services" className="hover:text-white transition-colors">Intelligent Automation</a></li>
              <li><a href="#services" className="hover:text-white transition-colors">Chaos Engineering</a></li>
              <li><a href="#services" className="hover:text-white transition-colors">Self-Healing Systems</a></li>
              <li><a href="#services" className="hover:text-white transition-colors">App Modernization</a></li>
              <li><a href="#training" className="hover:text-white transition-colors">Workshops & Hackathons</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-5 text-[13px]">
            <p>
              © {new Date().getFullYear()} {COMPANY.fullName}. All rights reserved.
            </p>
            <Link
              to="/privacy"
              className="hover:text-white transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-white transition-colors"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
            <Link
              to="/agent"
              className="hover:text-white transition-colors"
              data-testid="footer-agent-link"
            >
              Admin · Agent
            </Link>
          </div>
          <div className="flex items-center gap-2">
            {[
              { I: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/scatura-solutions' },
              { I: Twitter, label: 'Twitter', href: '#' },
              { I: Github, label: 'GitHub', href: 'https://github.com/scaturasolutions' },
              { I: Mail, label: 'Email', href: `mailto:${COMPANY.email}` },
            ].map(({ I, label, href }) => {
              const isExternal = href.startsWith('http');
              const isPlaceholder = href === '#';
              return (
                <a
                  key={label}
                  href={href}
                  target={isExternal ? '_blank' : undefined}
                  rel={isExternal ? 'noopener noreferrer' : undefined}
                  onClick={isPlaceholder ? (e) => e.preventDefault() : undefined}
                  aria-label={label}
                  className="w-9 h-9 rounded-xl border border-slate-800 flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-600 transition-colors"
                >
                  <I size={15} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
